import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Upload } from 'lucide-react';
import { useToast } from '../components/toast/ToastProvider';
import { GuidelineDraftEditor } from '../components/GuidelineDraftEditor';
import { AccuracyMetricsTable } from '../components/AccuracyMetricsSection';
import api from '../services/api';
import { guidelineResponseToDraftContent } from '../services/guidelineToDraftContent';
import { buildTuningDrilldownPath } from '../services/accuracyCountDrilldown';
import type { MetricCountDrilldown } from '../services/accuracyCountDrilldown';
import type { EvaluationRun, GuidelineDraftContent, TuningSession } from '../types/accuracy';
import {
  getBaselineReport as getBaselineReportApi,
  getEvaluationRun as getEvaluationRunApi,
  getGuidelineDraft as getGuidelineDraftApi,
  getTuningSession as getTuningSessionApi,
  listEvaluationRuns as listEvaluationRunsApi,
  publishTuningSession as publishTuningSessionApi,
} from '../services/accuracyBackendApi';
import { buildTuningRunDetailPath } from '../services/accuracyTuningPaths';
import type { DraftCompareTarget, DraftCompareTargetId } from '../services/accuracyTuningDraftSeed';
import { formatAccuracyStatusLabel } from '../services/accuracyStatusLabels';
import { draftHasCategories, normalizeDraftContent } from '../services/guidelineDraftMetadata';
import './AccuracyReportPage.css';
import './AccuracyTuningPages.css';

const AccuracyTuningRunDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { sessionId, runId } = useParams<{ sessionId: string; runId: string }>();
  const [apiSession, setApiSession] = useState<TuningSession | null>(null);
  const [apiRun, setApiRun] = useState<EvaluationRun | null>(null);
  const [apiRuns, setApiRuns] = useState<EvaluationRun[]>([]);
  const [apiBaselineName, setApiBaselineName] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const session = useMemo(
    () => {
      if (!sessionId) return undefined;
      return apiSession ?? undefined;
    },
    [sessionId, apiSession]
  );

  const run = useMemo(() => {
    if (!runId) return undefined;
    return apiRun ?? undefined;
  }, [runId, apiRun]);

  const baseline = useMemo(
    () => {
      if (!session) return undefined;
      return apiBaselineName ? ({ name: apiBaselineName } as any) : undefined;
    },
    [session, apiBaselineName]
  );

  const allRuns = useMemo(() => {
    if (!sessionId) return [];
    return apiRuns;
  }, [sessionId, apiRuns]);

  useEffect(() => {
    if (!sessionId || !runId) return;
    (async () => {
      try {
        const s = await getTuningSessionApi(sessionId);
        setApiSession(s);
        const r = await getEvaluationRunApi(runId);
        setApiRun(r);
        const runs = await listEvaluationRunsApi(sessionId);
        setApiRuns(runs);
        const baseline = await getBaselineReportApi(s.baseline_report_id);
        setApiBaselineName(baseline.name);
      } catch {
        // ignore
      }
    })();
  }, [sessionId, runId]);

  const previousRun = useMemo(() => {
    if (!run) return undefined;
    return allRuns.find((r) => r.run_number === run.run_number - 1);
  }, [allRuns, run]);

  const [productionBaselineDraft, setProductionBaselineDraft] = useState<
    GuidelineDraftContent | undefined
  >(undefined);
  const [resolvedDraftContent, setResolvedDraftContent] = useState<
    GuidelineDraftContent | undefined
  >(undefined);
  const [draftContentSource, setDraftContentSource] = useState<
    'snapshot' | 'live_draft' | 'production' | undefined
  >(undefined);
  const [compareTargetId, setCompareTargetId] = useState<DraftCompareTargetId>('baseline');
  const [nextPublishVersion, setNextPublishVersion] = useState<number | null>(null);

  useEffect(() => {
    if (!session?.base_guideline_id) {
      setNextPublishVersion(null);
      return;
    }
    (async () => {
      try {
        const resp = await api.guidelines.getGuidelineVersions(session.base_guideline_id);
        const versions = resp.versions ?? [];
        const maxVersion = versions.reduce((max, v) => Math.max(max, v.version), 0);
        setNextPublishVersion(maxVersion > 0 ? maxVersion + 1 : (session.base_guideline_version ?? 0) + 1);
      } catch {
        setNextPublishVersion((session.base_guideline_version ?? 0) + 1);
      }
    })();
  }, [session?.base_guideline_id, session?.base_guideline_version]);

  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        const g = await api.guidelines.getGuideline(
          session.base_guideline_id,
          session.base_guideline_version
        );
        setProductionBaselineDraft(
          guidelineResponseToDraftContent({
            guideline_id: session.base_guideline_id,
            base_version: session.base_guideline_version,
            guideline: g as any,
          })
        );
      } catch {
        setProductionBaselineDraft(undefined);
      }
    })();
  }, [session?.id, session?.base_guideline_id, session?.base_guideline_version]);

  useEffect(() => {
    if (!run || !session) {
      setResolvedDraftContent(undefined);
      setDraftContentSource(undefined);
      return;
    }

    const snapshot = run.draft_snapshot;
    if (draftHasCategories(snapshot)) {
      setResolvedDraftContent(normalizeDraftContent(snapshot!));
      setDraftContentSource('snapshot');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const draft = await getGuidelineDraftApi(session.draft_id);
        if (!cancelled && draftHasCategories(draft.content_json)) {
          setResolvedDraftContent(normalizeDraftContent(draft.content_json));
          setDraftContentSource('live_draft');
          return;
        }
      } catch {
        // fall through
      }
      if (!cancelled && productionBaselineDraft) {
        setResolvedDraftContent(productionBaselineDraft);
        setDraftContentSource('production');
        return;
      }
      if (!cancelled && snapshot) {
        setResolvedDraftContent(normalizeDraftContent(snapshot));
        setDraftContentSource('snapshot');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [run, session, productionBaselineDraft]);

  const compareTargets = useMemo((): DraftCompareTarget[] => {
    const targets: DraftCompareTarget[] = [];
    if (productionBaselineDraft) {
      targets.push({
        id: 'baseline',
        label: `本番ガイドライン（v${productionBaselineDraft.base_version}）`,
        content: normalizeDraftContent(productionBaselineDraft),
      });
    }
    if (previousRun?.draft_snapshot && draftHasCategories(previousRun.draft_snapshot)) {
      targets.push({
        id: 'previous_run',
        label: `前回評価（#${previousRun.run_number}）`,
        content: normalizeDraftContent(previousRun.draft_snapshot),
      });
    }
    return targets;
  }, [productionBaselineDraft, previousRun]);

  useEffect(() => {
    if (compareTargets.length === 0) return;
    if (!compareTargets.some((t) => t.id === compareTargetId)) {
      setCompareTargetId(compareTargets[0].id);
    }
  }, [compareTargets, compareTargetId]);

  const baselineReportHref = session?.baseline_report_id
    ? `/accuracy/reports/${encodeURIComponent(session.baseline_report_id)}`
    : '/accuracy';

  const baselineBackLabel = '評価レポート';

  const handleCountClick = useCallback(
    (drilldown: MetricCountDrilldown) => {
      if (!sessionId || !runId) return;
      navigate(
        buildTuningDrilldownPath({
          evaluationRunId: runId,
          tuningSessionId: sessionId,
          drilldown,
        })
      );
    },
    [navigate, sessionId, runId]
  );

  const publishedVersionForThisRun =
    run?.published_guideline_version ??
    (session?.published_from_evaluation_run_id === runId
      ? session?.published_guideline_version ?? undefined
      : undefined);

  const isThisRunPublishedToProduction = publishedVersionForThisRun != null;
  const canPublish = Boolean(run?.status === 'completed' && !isThisRunPublishedToProduction);

  const handlePublish = useCallback(async () => {
    if (!sessionId || !runId || !session || !run || publishing) return;
    if (isThisRunPublishedToProduction) return;
    if (run.status !== 'completed') {
      toast.warning('完了した評価のみ公開できます', { title: '公開できません' });
      return;
    }
    setPublishing(true);
    try {
      const updated = await publishTuningSessionApi({
        sessionId,
        evaluation_run_id: runId,
      });
      setApiSession(updated);
      const refreshedRun = await getEvaluationRunApi(runId);
      setApiRun(refreshedRun);
      if (updated.published_guideline_version != null) {
        setNextPublishVersion(updated.published_guideline_version + 1);
      }
      toast.success(
        `本番ガイドライン v${updated.published_guideline_version} として公開しました`,
        { title: '公開完了' }
      );
    } catch (e) {
      console.error('Tuning publish failed:', e);
      toast.error('ガイドラインの公開に失敗しました', { title: '公開失敗' });
    } finally {
      setPublishing(false);
    }
  }, [sessionId, runId, session, run, publishing, toast, isThisRunPublishedToProduction]);

  const nextGuidelineVersion =
    nextPublishVersion ?? (session?.base_guideline_version ?? 0) + 1;

  if (!sessionId || !runId || !session || !run) {
    return (
      <div className="accuracy-page">
        <Link to={baselineReportHref} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
        <div className="accuracy-card accuracy-empty">評価実行が見つかりません。</div>
      </div>
    );
  }

  const snapshot =
    resolvedDraftContent ??
    (run.draft_snapshot ? normalizeDraftContent(run.draft_snapshot) : undefined);
  if (!snapshot) {
    return (
      <div className="accuracy-page">
        <Link to={baselineReportHref} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          {baselineBackLabel}
        </Link>
        <div className="accuracy-card accuracy-empty">ガイドラインスナップショットがありません。</div>
      </div>
    );
  }
  const draftSourceNote =
    draftContentSource === 'live_draft'
      ? '評価実行時のスナップショットが空のため、現在のドラフトを表示しています。'
      : draftContentSource === 'production'
        ? '評価実行時のスナップショットが空のため、本番ガイドライン v'
          + `${session.base_guideline_version} を表示しています。`
        : '評価キュー投入時点のドラフト全文です。以降の編集はこの run には反映されません。';

  return (
    <div className="accuracy-page accuracy-tuning-run-detail">
      <Link to={baselineReportHref} className="accuracy-drilldown-back">
        <ArrowLeft size={16} />
        {baselineBackLabel}
      </Link>

      <header className="accuracy-header accuracy-tuning-run-detail-header" style={{ marginTop: 12 }}>
        <div className="accuracy-title">
          <h1>評価 #{run.run_number}</h1>
          <p>
            {session.name}
            {' · '}
            ベースライン: {baseline?.name || session.baseline_report_id}
            {' · '}
            本番 v{session.base_guideline_version}
            {run.matcher_version ? ` · マッチャ ${run.matcher_version}` : ''}
            {isThisRunPublishedToProduction ? (
              <>
                {' · '}
                <span className="accuracy-status completed">
                  本番反映済み v{publishedVersionForThisRun}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="accuracy-actions accuracy-tuning-run-header-actions">
          {isThisRunPublishedToProduction ? (
            <span className="accuracy-tuning-run-publish-note accuracy-tuning-run-publish-note--done">
              この評価を本番に反映済み
            </span>
          ) : (
            <button
              type="button"
              className="accuracy-button primary"
              disabled={!canPublish || publishing}
              title={
                run.status !== 'completed'
                  ? '評価完了後に公開できます'
                  : `評価 #${run.run_number} のガイドラインを本番 v${nextGuidelineVersion} として公開`
              }
              onClick={() => void handlePublish()}
            >
              {publishing ? (
                <Loader2 size={16} className="spin" style={{ marginRight: 6, verticalAlign: 'middle' }} />
              ) : (
                <Upload size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              )}
              本番ガイドラインとして公開
            </button>
          )}
        </div>
      </header>

      <div className="accuracy-card accuracy-tuning-run-meta">
        <dl className="accuracy-report-filters-summary">
          <div>
            <dt>ステータス</dt>
            <dd>
              <span className={`accuracy-status ${run.status}`}>
                {formatAccuracyStatusLabel(run.status)}
              </span>
            </dd>
          </div>
          <div>
            <dt>実行日時</dt>
            <dd>{new Date(run.created_at).toLocaleString('ja-JP')}</dd>
          </div>
          {run.completed_at && (
            <div>
              <dt>完了日時</dt>
              <dd>{new Date(run.completed_at).toLocaleString('ja-JP')}</dd>
            </div>
          )}
          <div>
            <dt>評価 URL 数</dt>
            <dd>{run.result?.url_count ?? '—'} 件</dd>
          </div>
        </dl>

        {allRuns.length > 1 && (
          <nav className="accuracy-tuning-run-nav" aria-label="同一セッションの評価">
            {allRuns.map((r) => (
              <Link
                key={r.id}
                to={buildTuningRunDetailPath(sessionId, r.id)}
                className={`accuracy-tuning-run-nav-item ${r.id === runId ? 'active' : ''}`}
              >
                評価 #{r.run_number}
              </Link>
            ))}
          </nav>
        )}
      </div>

      {run.result && (
        <div className="accuracy-card accuracy-section">
          <header className="accuracy-section-header">
            <h3>精度（この評価）</h3>
            <p className="accuracy-section-desc">
              件数セルをクリックすると URL ごとの内訳を開きます。
            </p>
          </header>
          <AccuracyMetricsTable
            result={run.result}
            onCountClick={handleCountClick}
            executionLabel={`チューニング #${run.run_number}`}
          />
        </div>
      )}

      <div className="accuracy-card accuracy-tuning-draft-editor-card">
        <header className="guideline-draft-detail-header">
          <h3>
            ガイドライン詳細（評価 #{run.run_number} · ベース v{snapshot.base_version}）
          </h3>
        </header>
        <p className="guideline-draft-snapshot-note">{draftSourceNote}</p>
        <GuidelineDraftEditor
          draft={snapshot}
          onChange={() => {}}
          readOnly
          compareTargets={compareTargets}
          compareTargetId={compareTargetId}
          onCompareTargetChange={setCompareTargetId}
          draftTitle={`評価 #${run.run_number} スナップショット`}
        />
      </div>
    </div>
  );
};

export default AccuracyTuningRunDetailPage;
